import { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';

import { PREFERENCE_FEEDBACK_MESSAGES } from '@shared/guidedPlanningPreferences';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { usePreferenceStepHandlers } from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { PreferenceFeedback } from '@/features/guided-financial-planning/components/preference-feedback';
import {
  RadioOptionCards,
  type RadioOption,
} from '@/features/guided-financial-planning/components/radio-option-cards';

const BENEFIT_STRUCTURE_OPTIONS: RadioOption<BenefitStructurePreference>[] = [
  {
    value: 'max-value',
    title: 'Most Bang for Your Buck',
    description: 'Higher benefits, but use-it-or-lose-it',
  },
  {
    value: 'guaranteed',
    title: 'Guaranteed Benefits',
    description: 'Always get something back, even if unused',
  },
];

export function PreferencesBenefitStructureStep() {
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();

  const sessionValue = session?.stepData?.preferences?.benefitStructure;
  const [benefitStructure, setBenefitStructure] = useState<BenefitStructurePreference | undefined>(
    sessionValue,
  );

  useEffect(() => {
    if (sessionValue && !benefitStructure) {
      setBenefitStructure(sessionValue);
    }
  }, [sessionValue, benefitStructure]);

  const { handleNext, handleBack, isSaving } = usePreferenceStepHandlers({
    preferenceKey: 'benefitStructure',
    value: benefitStructure,
  });

  const isValid = benefitStructure !== undefined;

  const feedbackMessage = benefitStructure
    ? PREFERENCE_FEEDBACK_MESSAGES.benefitStructure[benefitStructure]
    : null;

  return (
    <StepLayout
      title="What type of benefit structure?"
      subtitle="Choose between maximum value or guaranteed returns."
      icon={
        <div className="flex flex-col items-center gap-3">
          <span className="text-sm font-medium text-gray-500">Question 3 of 3</span>
          <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
            <Shield className="text-primary size-7" />
          </div>
        </div>
      }
      nextButtonLabel="See My Predicted Preferences"
      isNextDisabled={!isValid || isSaving}
      onNext={handleNext}
      onBack={handleBack}
      isLoading={isSaving}
    >
      <div className="flex flex-col items-center">
        <RadioOptionCards
          options={BENEFIT_STRUCTURE_OPTIONS}
          value={benefitStructure}
          onChange={setBenefitStructure}
          className="w-full max-w-lg"
          disabled={isSessionComplete}
        />
        <div className="w-full max-w-lg">
          <PreferenceFeedback message={feedbackMessage} />
        </div>

        <div className="mt-8 flex justify-center gap-2">
          <div className="size-2 rounded-full bg-gray-300" />
          <div className="size-2 rounded-full bg-gray-300" />
          <div className="bg-primary size-2 rounded-full" />
        </div>
      </div>
    </StepLayout>
  );
}
